import { Beacon as DiceBeacon } from '@dicetechnology/dice-unity/lib/services/Beacon/index';
import { BeaconType, EBeaconAction } from '@dicetechnology/dice-unity/lib/services/Beacon/types';
import type { Http } from '@dicetechnology/dice-unity/lib/services/Http/index';
import autoBind from 'auto-bind';

import { BEACON_ERRORS, TOKEN_REFRESH_ERROR } from '../../constants';
import type { BeaconErrorCallback, ErrorDetails } from '../../types';
import { isRefreshTokenUrl } from '../../utils';

export class Beacon {
    private _beacon: DiceBeacon;
    private _lastBeaconSent: boolean = false;
    private _onErrorCallback: BeaconErrorCallback;

    constructor(http: Http, url: string, onError: BeaconErrorCallback) {
        autoBind(this);

        this._onErrorCallback = onError;
        this._beacon = new DiceBeacon({
            http,
            url,
            onError: this.onError
        });

        // Heart beats will be sent automatically after created.
        // Call `stop` to set `_stopped` false to disable HB triggering until `start` is called.
        this._beacon.stop();
    }

    public configure(videoId: number, watchProgress: number, action: EBeaconAction): void {
        this._beacon.video = {
            id: videoId,
            watchProgress,
            type: action === EBeaconAction.LIVE ? BeaconType.LIVE : BeaconType.VOD
        };
    }

    public getAction(): EBeaconAction | undefined {
        return this._beacon.state.action;
    }

    public getCorrelationId(): string | undefined {
        return this._beacon.state.cid;
    }

    public getVideoId(): string | undefined {
        return this._beacon.video?.id?.toString();
    }

    public start(): void {
        this._beacon.start();
        this._beacon.send();
    }

    public stop(last?: boolean): void {
        if (last) {
            if (this._lastBeaconSent) {
                return;
            }
            this._lastBeaconSent = true;
        }
        this._beacon.stop(last);
    }

    public updateProgress(currentTime: number): void {
        this._beacon.progress = currentTime;
    }

    private onError({ status, url }: Response): void {
        const error = BEACON_ERRORS[status] as ErrorDetails | undefined; // Inform TypeScript this can be undefined
        const isRefreshError = isRefreshTokenUrl(url);

        if (error || isRefreshError) {
            this.stop();
            this._onErrorCallback({
                code: error?.code ?? TOKEN_REFRESH_ERROR.code,
                message: error?.message ?? TOKEN_REFRESH_ERROR.message,
                title: error?.title ?? TOKEN_REFRESH_ERROR.title
            });
        }
    }
}
